import { useEffect, useLayoutEffect, type CSSProperties, type MouseEvent } from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import { t, useLanguage } from '@/lib/i18n'
import { BottomBar } from './BottomBar'
import { usePlayOnce } from './motion'
import { Toaster } from './Toast'
import { SampleChip } from './SampleChip'
import { TopBar } from './TopBar'
import { PHONE_QUERY, useMediaQuery } from './use-media-query'
import { notifyRouteCommitted } from './view-transition'

/** Dev only: more than one `data-r5-accent` element on a screen breaks the one-yellow-button rule. */
export function useAccentWarning(pathname: string) {
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const timer = window.setTimeout(() => {
      const accents = document.querySelectorAll('[data-r5-accent]')
      if (accents.length > 1) console.warn(`[r5] ${accents.length} accent (yellow) buttons on ${pathname}: a screen has one`, accents)
    }, 800)
    return () => window.clearTimeout(timer)
  }, [pathname])
}

/**
 * The page entrance (APP-DESIGN-DIRECTION 3.9): the first visit of a route in a session plays the full cascade; later visits
 * get the route transition only. Put the returned attribute on the page's container; mark each top-level section with
 * `enterProps(n)`. Without the motion gate nothing is parked, so nothing waits on an animation.
 */
export function usePageEnter(key: string): { 'data-entering': 'true' | 'false' } {
  const play = usePlayOnce('enter', key)
  useEffect(() => { if (play.play) play.seen() }, [play])
  return { 'data-entering': play.play ? 'true' : 'false' }
}

/** Marks a top-level section for the page entrance, `index` 0 first (capped at six by the CSS). */
export function enterProps(index: number): { 'data-enter': ''; style: CSSProperties } {
  return { 'data-enter': '', style: { ['--r5-e' as string]: index } }
}

/**
 * The frame of every signed-in student page, in place of `LucentShell`: a skip link, `TopBar`, the page in `<main id="main">`
 * and, at 760px and below, `BottomBar`. Root class is `.r5 .r5-shell`, so every token and component style applies below
 * it and nothing leaks out (it is never an ancestor of the exam runner or the admin shell).
 *
 * It also lets a pending route View Transition capture the new page (`notifyRouteCommitted`), and in development warns if
 * a screen mounts more than one accent button.
 */
export function R5Shell() {
  useLanguage()
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const phone = useMediaQuery(PHONE_QUERY)
  const { demo } = useAuth()
  // A new page opens at its top (back and forward keep the browser's own position); scrolling before the commit is
  // reported means the view transition captures the top of the new page, not wherever the last page was scrolled to.
  useLayoutEffect(() => {
    if (navigationType !== 'POP') window.scrollTo?.(0, 0)
    notifyRouteCommitted()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  useAccentWarning(pathname)
  const entering = usePageEnter(pathname)

  const skip = (event: MouseEvent<HTMLAnchorElement>) => {
    // A hash jump would put "#main" in the URL and the router history; move focus instead.
    event.preventDefault()
    const main = document.getElementById('main')
    main?.focus()
    main?.scrollIntoView?.({ block: 'start' })
  }

  return (
    <div className="r5 r5-shell" data-density="comfy">
      <a className="r5-skip" href="#main" onClick={skip}>{t('Skip to content')}</a>
      <TopBar />
      {demo && <SampleChip layout="strip" />}
      <main id="main" className="r5-main" tabIndex={-1} {...entering}>
        <Outlet />
      </main>
      {phone && <BottomBar />}
      <Toaster />
    </div>
  )
}
